// --- Imports ---
import {
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  IconButton,
} from "@mui/material";
import { CheckCircleOutline, Code } from "@mui/icons-material";
import { motion } from "framer-motion";
import React from "react";

// --- Hero Content Component ---
export default function HeroContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme?.breakpoints?.down("lg"));

  // --- Content Data ---
  const content = {
    quote:
      "Programming for me is like solving a puzzle; each component is a piece, and the joy comes when they all work together!",
    introduction: {
      prefix: "INITIATING SYSTEM",
      name: "POURIYA SABOURI",
      suffix:
        "Specialized in creating high-performance, immersive, and next-generation web applications.",
    },
    features: [
      "JavaScript (ES6+)",
      "React.js",
      "Next.js",
      "Redux Toolkit",
      "Framer Motion",
      "Node.js",
    ],
  };

  // --- Initial Load Animation Variants ---
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50, scale: 0.9 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 120, damping: 14 },
    },
  };

  const featureVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      filter: "blur(10px)",
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
      },
    },
  };

  return (
    <Stack
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      sx={{
        flex: 1,
        maxWidth: { xs: "100%", lg: "650px" },
        alignItems: isMobile ? "center" : "flex-start",
        textAlign: isMobile ? "center" : "left",
        gap: 3.5,
        position: "relative",
      }}
    >
      {/* --- Intro Badge --- */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 0.8,
          borderRadius: "30px",
          border: `1px solid ${theme?.palette?.primary?.main}50`,
          background: `linear-gradient(90deg, ${theme?.palette?.primary?.dark}40, transparent)`,
          boxShadow: `0 0 20px ${theme?.palette?.primary?.main}20`,
        }}
      >
        <Box
          component={motion.span}
          animate={{ opacity: [1, 0.2, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: theme?.palette?.primary?.main,
            boxShadow: `0 0 10px ${theme?.palette?.primary?.main}`,
          }}
        />
        <Typography
          sx={{
            color: theme?.palette?.primary?.light,
            fontSize: "0.75rem",
            letterSpacing: 3,
            fontWeight: 700,
          }}
        >
          {content?.introduction?.prefix}
        </Typography>
      </Box>

      {/* --- Main Name --- */}
      <Typography
        component={motion.h1}
        variants={itemVariants}
        sx={{
          fontSize: { xs: "2.5rem", md: "4rem", lg: "5rem" },
          fontWeight: 900,
          lineHeight: 1,
          textTransform: "uppercase",
          background: `linear-gradient(to right, #ffffff, ${theme?.palette?.primary?.light}, ${theme?.palette?.secondary?.light})`,
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
          filter: `drop-shadow(0 0 30px ${theme?.palette?.primary?.main}50)`,
          letterSpacing: "-2px",
        }}
      >
        {content?.introduction?.name}
      </Typography>

      <Typography
        component={motion.p}
        variants={itemVariants}
        sx={{
          fontSize: { xs: "1rem", lg: "1.2rem" },
          lineHeight: 1.7,
          color: theme?.palette?.text?.secondary,
          maxWidth: "550px",
          fontWeight: 300,
        }}
      >
        {content?.introduction?.suffix}
      </Typography>

      {/* --- Quote --- */}
      <Typography
        component={motion.p}
        variants={itemVariants}
        sx={{
          position: "relative",
          pl: 3,
          py: 1,
          fontStyle: "italic",
          color: theme?.palette?.text?.primary,
          borderLeft: `3px solid ${theme?.palette?.secondary?.main}`,
          background: `linear-gradient(90deg, ${theme?.palette?.secondary?.main}10, transparent)`,
          borderRadius: "0 10px 10px 0",
        }}
      >
        "{content?.quote}"
      </Typography>

      {/* --- Pure CSS Features/Skills Badges --- */}
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1.5}
        justifyContent={isMobile ? "center" : "flex-start"}
        sx={{ mt: 2 }}
      >
        {content?.features?.map((item, i) => (
          <Box
            key={item}
            component={motion.div}
            variants={itemVariants}
            whileHover={{
              scale: 1.05,
              y: -3,
              boxShadow: `0 10px 20px ${theme.palette.primary.main}`,
              transition: { type: "spring", stiffness: 400 },
            }}
            whileTap={{ scale: 0.95 }}
            sx={{
              px: 2,
              py: 0.8,
              borderRadius: 20,
              fontSize: "0.85rem",
              border: `1px solid ${theme.palette.primary.main}`,
              background: `linear-gradient(145deg, 
                          ${theme.palette.background.paper}80, 
                          ${theme.palette.background.default}80)`,
              backdropFilter: "blur(10px)",
              cursor: "pointer",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                transition: "left 0.5s",
              },
              "&:hover::before": {
                left: "100%",
              },
            }}
          >
            {item}
          </Box>
        ))}
      </Stack>

      {/* --- Reactor Code Button --- */}
      <IconButton
        component={motion.button}
        variants={itemVariants}
        whileHover={{ scale: 1.15, rotate: 180 }}
        whileTap={{ scale: 0.85 }}
        sx={{
          mt: 3,
          width: 70,
          height: 70,
          background: `radial-gradient(circle, ${theme?.palette?.primary?.dark} 0%, transparent 80%)`,
          border: `2px dashed ${theme?.palette?.primary?.main}`,
          color: theme?.palette?.primary?.light,
          boxShadow: `0 0 30px ${theme?.palette?.primary?.main}40`,
          "&:hover": {
            background: `radial-gradient(circle, ${theme?.palette?.primary?.main} 0%, transparent 80%)`,
            boxShadow: `0 0 50px ${theme?.palette?.primary?.main}80`,
            border: `2px solid ${theme?.palette?.primary?.light}`,
          },
        }}
      >
        <Code sx={{ fontSize: 36 }} />
      </IconButton>
    </Stack>
  );
}
